import type { NotificationToast } from "@common/services/common-events";
import { CommonEvents } from "@common/services/common-events";
import ImperativeModal from "@web/src/app/components/imperative-modal";
import type { ModalAction } from "@web/src/app/entities/imperative-modal";
import { ThemeProvider } from "@web/src/app/components/theme-provider";
import { analytics } from "@web/src/app/use-cases/analytics";
import { TourEvents } from "@web/src/app/use-cases/onboarding";
import mockStores from "@web/src/app/use-cases/onboarding/mock-stores";
import { useTutorial } from "@web/src/app/use-cases/onboarding/useTutorial";
import { useEvent } from "@web/src/hooks/useEvent";
import { NotificationToasts, withNotificationToasts } from "@web/src/hooks/useNotificationToasts";
import stores from "@web/src/store/web-stores";
import "@web/src/styles/animations.scss";
import "@web/src/styles/dialog.scss";
import "@web/src/styles/global-command.scss";
import "@web/src/styles/globals.scss";
import "@web/src/styles/colors.scss";
import "@web/src/styles/popover.scss";
import "@web/src/styles/toast.scss";
import "@web/src/app/use-cases/styles/inject-colors";
import { MobXProviderContext, observer } from "mobx-react";
import type { AppProps } from "next/app";
import type { ComponentProps, ComponentType, FC } from "react";
import { GoogleOAuthProvider } from "@react-oauth/google";

/** @react-oauth/google types return JSX.Element; React 19 expects ReactNode | Promise<ReactNode>. */
const GoogleOAuthRoot = GoogleOAuthProvider as unknown as ComponentType<
  ComponentProps<typeof GoogleOAuthProvider>
>;

const MyApp: FC<AppProps & JSX.IntrinsicAttributes> = observer(function MyApp({ Component, pageProps }) {
  const { isTutorial } = useTutorial();

  // In order to leverage cleanup from hotreload
  useEvent(CommonEvents, "PAYWALL", (opts) => {
    stores.purchaseStore.setPaywallOpen(true);
    analytics.track("paywall_open", opts);
  });

  const mapModalActionColor: Record<Required<Required<NotificationToast>["action"]>["color"], ModalAction["intent"]> = {
    negative: "DANGER",
    info: "PRIMARY",
    positive: "PRIMARY",
    warning: "WARNING",
  };

  useEvent(CommonEvents, "LOGOUT", () => stores.userStore.logout(), [stores.userStore]);
  useEvent(CommonEvents, "SHOW_MODAL", (modal) => stores.uiStore.setModalProps(modal));
  useEvent(CommonEvents, "PUBLISH_ALERT", (alert) => {
    const action: ModalAction = alert.action
      ? ({
          intent: mapModalActionColor[alert.action.color ?? "info"],
          onClick: alert.action.onClick,
          text: alert.action.label,
        } satisfies ModalAction)
      : {
          intent: "PRIMARY",
          onClick: () => {},
          text: "Okay",
        };
    stores.uiStore.setModalProps({
      title: alert.title,
      content: alert.content,
      actions: [action],
    });
  });

  useEvent(TourEvents, "ON_REGISTER_SEEN", () => stores.userStore.setNotBrandNew());

  return (
    <GoogleOAuthRoot clientId={process.env.NEXT_PUBLIC_GOOGLE_WEB_CLIENT_ID!}>
      <MobXProviderContext.Provider value={isTutorial ? mockStores : stores}>
        <ThemeProvider>
          {/* Notifications must be registered before useJoinShareLink is registered */}
          <NotificationToasts />
          <ImperativeModal />
          <Component {...pageProps} />
        </ThemeProvider>
      </MobXProviderContext.Provider>
    </GoogleOAuthRoot>
  );
});

// Notifications must be registered before useJoinShareLink is registered
export default withNotificationToasts(MyApp);
